import React, { memo, useState } from "react";
import { Avatar, Typography, Divider, Box, Button } from "@mui/material";
import {
  Assignment,
  Settings,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "@/lib/toast";
import { clear } from "@/store/slices/globalSlice";
import { useAppDispatch } from "@/store/hooks";
import { usePermissions } from "@/hooks/usePermissions";

const ProfileDropdown: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser, permissionLoading } = usePermissions();

  const logout = async () => {
    try {
      const returnUrl = location.pathname + location.search + location.hash;
      dispatch(clear());
      toast.success("Logged out successfully");

      navigate("/auth/login", {
        state: {
          returnUrl,
        },
        replace: true,
      });
    } catch (error) {
      toast.error("Something went wrong during logout");
    }
  };

  if (permissionLoading) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-[400px] flex flex-col overflow-hidden">
      {/* Top Section */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{
          background: "linear-gradient(135deg, rgb(var(--primary-color)) 0%, rgba(var(--primary-color), 0.8) 100%)",
          color: "white",
        }}
      >
        <Avatar alt="User Name" sx={{ width: 56, height: 56 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {currentUser?.userDetail?.data?.Name}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {currentUser?.userDetail?.data?.Email}
          </Typography>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto">
        {/* My Profile */}
        <Box
          p={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className="cursor-pointer hover:bg-gray-50 rounded-lg transition"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Settings sx={{ color: "#00BFA6" }} />
            <Box>
              <Typography fontWeight={600}>My Profile</Typography>
              <Typography variant="body2" color="text.secondary">
                Account settings and more
              </Typography>
            </Box>
          </Box>
          <KeyboardArrowRight sx={{ color: "gray" }} />
        </Box>

        <Divider />

        {/* My Tasks */}
        <Box
          p={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className="cursor-pointer hover:bg-gray-50 rounded-lg transition"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Assignment sx={{ color: "#7B61FF" }} />
            <Box>
              <Typography fontWeight={600}>My Tasks</Typography>
              <Typography variant="body2" color="text.secondary">
                Latest tasks and projects
              </Typography>
            </Box>
          </Box>
          <KeyboardArrowRight sx={{ color: "gray" }} />
        </Box>
      </Box>

      <Divider />

      {/* Sign Out */}
      <Box p={3}>
        <Button onClick={logout} variant="outlined">
          SIGN OUT
        </Button>
      </Box>
    </div>
  );
};

export default memo(ProfileDropdown);
